// Copyright (c) StrangeBee 2024

import React, { ReactElement, useMemo } from 'react';
import { TaskOutput, TaskStatus } from 'thehive-sdk';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { classList, useLocalMessage } from '@Helpers/';
import { ICONS } from '@Constants/iconsApp';

import './TaskDisplayStatus.scss';

export const TaskStatusElements: Record<TaskStatus, ReactElement> = {
    [TaskStatus.COMPLETED]: (
        <ICONS.CheckCircleOutlined className={classList('task-display-status__icon', 'icon__completed')} />
    ),
    [TaskStatus.IN_PROGRESS]: (
        <ICONS.StartIcon className={classList('task-display-status__icon', 'icon__in-progress')} />
    ),
    [TaskStatus.WAITING]: (
        <ICONS.ClockCircleOutlined className={classList('task-display-status__icon', 'icon__waiting')} />
    ),
    [TaskStatus.CANCEL]: <ICONS.CloseCircleIcon className={classList('task-display-status__icon', 'icon__cancel')} />,
};

interface TaskStatusProps {
    status?: TaskOutput['status'];
    displayLabel?: boolean;
    displayTooltip?: boolean;
    help?: string;
    onClick?: () => void;
}

export const TaskDisplayStatus = ({
    status = TaskStatus.IN_PROGRESS,
    displayLabel,
    help = '',
    displayTooltip = true,
    onClick,
}: TaskStatusProps): ReactElement => {
    const i = useLocalMessage();

    const display = useMemo(
        () =>
            displayLabel ? (
                <span className="task-display-status__label">{i('task.my-tasks.const.' + status)}</span>
            ) : (
                ''
            ),
        [displayLabel, status, i],
    );
    const displayHelp = useMemo(
        () => (help.trim() !== '' ? <span className="task-display-status__label-help">{help}</span> : ''),
        [help],
    );

    return (
        <div
            className={classList('task-display-status', { 'task-display-status__clickable': onClick })}
            onClick={onClick}
        >
            {displayTooltip ? (
                <THTooltip title={i('task.my-tasks.status', { status })}>{TaskStatusElements[status]}</THTooltip>
            ) : (
                TaskStatusElements[status]
            )}
            {display}
            {displayHelp}
        </div>
    );
};
